// Tipi del portale fatturazione — specchio di ProposalRead (backend Task 8).

export type DocType = "SF" | "FA" | "OSS" | "FA41" | "FA8" | "ST" | "RC" | "RCO";

/**
 * Sigle valide per kind (finding review T8): un'emissione non puo' diventare
 * una nota di credito (ST/RC/RCO) e viceversa. Condiviso da MarkDialog
 * (select filtrata) e ManualDialog (sempre kind=emission lato backend).
 */
export const DOC_TYPES_BY_KIND: Record<"emission" | "credit_note", DocType[]> = {
  emission: ["SF", "FA", "OSS", "FA41", "FA8"],
  credit_note: ["ST", "RC", "RCO"],
};

export type ProposalStatus =
  | "da_fatturare"
  | "in_revisione"
  | "esclusa"
  | "fatturata"
  | "da_inviare"
  | "in_attesa_spedizione"
  | "in_attesa_pagamento";

export interface Proposal {
  id: number;
  order_ref: string | null;
  channel: string;
  kind: "emission" | "credit_note";
  parent_proposal_id: number | null;
  doc_type: DocType | null;
  status: ProposalStatus;
  review_reason: string | null;
  exclusion_reason: string | null;
  invoice_date: string | null;
  order_date: string | null;
  customer_name: string;
  country: string;
  is_company: boolean;
  /** Tipo anagrafico Eureka derivato dal backend: S società, D ditta individuale, P privato, E ente. */
  tipo_anagrafico?: "S" | "D" | "P" | "E" | null;
  partial_shipment: boolean;
  // Pydantic v2 serializza Decimal come STRINGA JSON (verificato con
  // TestClient): mai number puro. Convertire con Number() lato consumer,
  // stesso pattern di ordini/api.ts total_amount.
  gross_expected: string | number | null;
  document_number: string | null;
  document_date: string | null;
  refund_date: string | null;
  refund_amount: string | number | null;
  invoice_notes: string | null;
  events: Array<{ at: string; event: string; detail: string }>;
  /** Round 2/3 — opzionali: i fixture dei test non li richiedono. */
  billing_source?: string | null;
  billing_final?: boolean;
  billing?: Record<string, unknown> | null;
  anagrafica?: Record<string, unknown> | null;
  anagrafica_verified?: boolean;
  eureka_check?: string | null;
  tot_ivato_verified?: string | number | null;
  /**
   * Termine di trasmissione allo SdI (art. 21 c.4 DPR 633/72), DERIVATO dal
   * backend (`sdi_deadline`): `"giallo"` dal 10° giorno, `"rosso"` dal 12°.
   * `sdi_giorni` è valorizzato solo quando c'è un alert.
   */
  sdi_alert?: "giallo" | "rosso" | null;
  sdi_giorni?: number | null;
  /**
   * Metodo di pagamento dichiarato dal canale, già in forma leggibile
   * («Bonifico», «PayPal», «Stripe»). Sul SITO sostituisce il nome del canale
   * nella colonna: lì «Bob Gardens» è uguale su ogni riga e non informa.
   * Sui marketplace resta assente o non viene usato.
   */
  payment_method?: string | null;
  /**
   * `true` (FA) = la mancata trasmissione è un FATTO: esiste il segnale FAD.
   * `false` (OSS/RC/RCO) = è una DICHIARAZIONE umana, quel segnale non esiste.
   * La differenza deve VEDERSI: è così che la OSS 110052 è rimasta invisibile
   * per 49 giorni.
   */
  sdi_verificabile?: boolean;
  /**
   * Codice STABILE del motivo di revisione, calcolato dal backend
   * (`classifier.review_code`). Serve alla UI per spiegare il motivo senza
   * matchare sul testo libero di `review_reason`, che cambia. `"altro"` o
   * assente → si mostra il testo grezzo (vedi ReviewReason.tsx).
   */
  review_code?: string | null;
  /**
   * Stato al REGISTRO IMPRESE quando non è «attiva» (inattiva, cessata,
   * sospesa, liquidazione). ⚠️ Non è lo stato della partita IVA: «inattiva»
   * dice che l'impresa non ha dichiarato l'inizio attività al REA, e da sola
   * non blocca la fattura (07/08, COFFEE BREAK SRLS: REA inattiva ma VIES
   * valido). La UI la mostra come chip informativo, non come motivo.
   */
  registro_stato?: string | null;
  /**
   * Id del TENTATIVO di emissione (`document_attempts.id`) da cui si stampa la
   * fattura di cortesia. È la chiave dei due endpoint `/documents/{id}/pdf` e
   * `/documents/{id}/metro-upload`: il PDF nasce dalla risposta di Eureka
   * conservata nel diario, non dalla proposta.
   *
   * Assente su tutte le righe il cui numero NON viene da un'emissione fatta
   * dal portale (numero dedotto dal registro, import storico, riga segnata a
   * mano): lì non esiste una risposta da cui stampare, e infatti il backend
   * risponderebbe 404. La UI mostra le azioni spente col motivo, invece di
   * nasconderle: un tasto che sparisce non dice perché.
   */
  document_attempt_id?: number | null;
}

/**
 * Esito del caricamento della fattura su Metro (`POST
 * /documents/{id}/metro-upload`). Le tre liste contengono gli id delle RIGHE
 * d'ordine: l'endpoint Metro è per riga, quindi lo stesso PDF va su ognuna.
 *
 * 🔑 `completato: false` non è un errore: sono le righe che Metro non ha ancora
 * marcato spedite. `descrizione` è la frase del backend, già in italiano.
 */
export interface EsitoMetroUpload {
  caricate: string[];
  gia_presenti: string[];
  non_spedite: string[];
  completato: boolean;
  descrizione: string;
}

/** Esito per riga di `POST /documents/create` (anteprima o creazione vera). */
export interface CreateDocumentsRow {
  proposal_id: number;
  order_ref: string | null;
  customer_name: string;
  doc_type: DocType | null;
  invoice_date: string | null;
  gross_expected: string | number | null;
  /** true = il backend è disposto a crearla (tutte le guardie passate). */
  ok: boolean;
  /** Perché NON è creabile, in italiano. Valorizzato solo se `ok` è false. */
  blocco: string | null;
  /** Numero assegnato da Eureka — solo dopo la creazione vera. */
  numero?: string | null;
  /**
   * La data che il documento AVRÀ (in anteprima) o ha (dopo la creazione).
   * Non coincide con `invoice_date` quando il documento è in ritardo: la
   * regola #3 lo fa slittare alla data dell'ultimo della sua serie, perché
   * numero e data devono crescere insieme.
   */
  data_documento?: string | null;
  /** Errore emerso durante la creazione vera (la riga era `ok` in anteprima). */
  errore?: string | null;
}

export interface CreateDocumentsResult {
  dry_run: boolean;
  righe: CreateDocumentsRow[];
}

/**
 * Stato di una riga nel diario di emissione (`DocumentAttempt.stato`).
 *
 * `non_elaborata` e `interrotto` non sono errori della riga: la prima è una
 * riga che il batch non ha mai raggiunto perché si è fermato prima, la seconda
 * una riga aperta al momento di un riavvio del container. Vanno distinte da
 * `errore`, altrimenti si va a cercare un problema su un documento che non è
 * mai stato tentato.
 */
export type BatchRowStato =
  | "in_attesa"
  | "in_corso"
  | "creato"
  | "bloccato"
  | "errore"
  | "non_elaborata"
  | "interrotto";

export interface BatchRow {
  proposal_id: number;
  /** Ordine di emissione (1-based): è l'ordine per data, la regola #14. */
  posizione: number;
  stato: BatchRowStato;
  doc_type: string | null;
  order_ref: string | null;
  customer_name: string;
  /** Decimal serializzato come stringa da Pydantic (vedi `Proposal`). */
  importo: string | null;
  numero: string | null;
  data_documento: string | null;
  eureka_doc_id: number | null;
  messaggio: string | null;
  durata_ms: number | null;
  /** Solo con `?completo=true`: payload spedito e risposta GREZZA di Eureka. */
  payload?: Record<string, unknown> | null;
  risposta?: unknown;
}

export interface BatchStatus {
  batch_id: string;
  /** true finché almeno una riga è `in_attesa` o `in_corso`. */
  in_corso: boolean;
  totale: number;
  creati: number;
  /** bloccate + errori + non elaborate + interrotte: tutto ciò che non nasce. */
  falliti: number;
  righe: BatchRow[];
}

/** PATCH /proposals/{id} — solo campi passati; ammesso solo senza documento. */
export interface EditBody {
  customer_name?: string;
  country?: string;
  gross_expected?: number | null;
  invoice_date?: string;
  company?: string;
  vat_number?: string;
  fiscal_code?: string;
  ragione_sociale?: string;
  sdi?: string;
  pec?: string;
  indirizzo?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  invoice_notes?: string;
  /** Sigla scelta a mano = decisione presa: azzera il motivo di revisione. */
  doc_type?: DocType;
  /** Solo su kind=credit_note (il backend rifiuta altrove). */
  refund_amount?: number | null;
  refund_date?: string;
}

export interface Counts {
  da_fatturare: number;
  in_revisione: number;
  da_inviare: number;
  nc_pendenti: number;
  in_attesa: number;
  escluse: number;
  fatturate: number;
  /** Totale righe, qualunque kind/stato (tab «Tutte»). */
  tutte: number;
}

/** Su quale data lavora il filtro periodo (whitelist backend `PERIODO_CAMPI`).
 *
 *  Sono tre domande diverse sullo stesso archivio: una SF emessa il 1° luglio
 *  su un ordine del 27 giugno sta in giugno per chi guarda le vendite e in
 *  luglio per chi fa la liquidazione IVA. */
export type CampoData = "order" | "invoice" | "document";

/** Colonne ordinabili lato server (whitelist backend `_SORTABLE`). */
export type SortField =
  | "invoice_date"
  | "order_date"
  | "customer_name"
  | "gross_expected"
  | "channel"
  | "doc_type"
  | "status"
  | "document_number"
  | "document_date";

export interface SyncNowResult {
  detect: { created: number; scanned: number };
  nc: { created: number };
  enrich: Record<string, number>;
  backfill: { filled: number; generic_excluded: number };
}

/** Documento emesso su un ordine rimborsato senza storno in Eureka (26/10,
 *  18114942-A). Lo calcola il backend a ogni caricamento: 0 nel giorno buono. */
export interface StornoScoperto {
  proposal_id: number;
  ordine: string;
  documento: string;
  cliente: string;
  importo: string;
  nc_id: number | null;
  motivo: string;
}

export interface WorklistResponse {
  items: Proposal[];
  counts: Counts;
  storni_scoperti?: StornoScoperto[];
}

export interface MarkBody {
  doc_type: DocType;
  document_number: string;
  document_date: string;
  status: "fatturata" | "da_inviare";
}

export interface ManualBody {
  order_ref?: string | null;
  channel?: string;
  customer_name: string;
  country: string;
  gross_expected?: number | null;
  invoice_date: string;
  doc_type?: DocType | null;
}
